import { Box, Stack, Typography } from '@mui/material';
import AddRounded from '@mui/icons-material/AddRounded';
import ErrorOutlineRounded from '@mui/icons-material/ErrorOutlineRounded';
import RemoveRounded from '@mui/icons-material/RemoveRounded';
import CreditIcon from 'src/assets/icons/CreditIcon.svg';
import AiCreditIcon from 'src/assets/icons/AiCreditIcon.svg';
import Amount from './Amount';
import {
  BuyButton,
  PackCard,
  PackHeader,
  PackStepperRow,
  PackTitle,
  ShortfallPanel,
  StepButton,
  StepperGroup,
  SummaryDivider,
  SummaryRow,
} from './styles/BuyStep.styles';
import { AI_CREDIT_INK, AI_CREDIT_INK_DARK, BuyStepProps, inr } from './types';

const BuyStep = ({
  packSize,
  packCost,
  maxPacks,
  qty,
  setQty,
  credits,
  isLoading,
  mobile,
  onBuy,
  onGetCredits,
}: BuyStepProps) => {
  const total = qty * packCost;
  const gain = qty * packSize;
  const short = Math.max(0, total - credits);

  return (
    <Stack sx={{ p: mobile ? '2px 20px 22px' : '0 24px 24px', gap: 1.75 }}>
      <PackCard>
        <PackHeader>
          <Box
            component="img"
            src={AiCreditIcon}
            alt=""
            sx={{ height: 34, width: 34 }}
          />
          <Stack sx={{ gap: 0.25, flex: 1 }}>
            <PackTitle>{inr(packSize)} AI Credits</PackTitle>
            <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
              One pack · enough for {inr(packSize)} AI actions
            </Typography>
          </Stack>
          <Amount icon={CreditIcon} size={19} fontSize={14}>
            {packCost}
          </Amount>
        </PackHeader>
        <PackStepperRow>
          <Typography
            sx={{ fontSize: 12.5, fontWeight: 600, color: 'text.secondary' }}
          >
            Packs
          </Typography>
          <StepperGroup>
            <StepButton
              disabled={qty <= 1}
              onClick={() => setQty(qty - 1)}
              aria-label="One pack fewer"
            >
              <RemoveRounded sx={{ fontSize: 16 }} />
            </StepButton>
            <Typography
              sx={{
                minWidth: 26,
                textAlign: 'center',
                fontSize: 14.5,
                fontWeight: 800,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {qty}
            </Typography>
            <StepButton
              disabled={qty >= maxPacks}
              onClick={() => setQty(qty + 1)}
              aria-label="One pack more"
            >
              <AddRounded sx={{ fontSize: 16 }} />
            </StepButton>
          </StepperGroup>
        </PackStepperRow>
      </PackCard>

      <Stack sx={{ gap: 1, px: 0.25 }}>
        <SummaryRow>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            You get
          </Typography>
          <Amount
            icon={AiCreditIcon}
            color={AI_CREDIT_INK}
            colorDark={AI_CREDIT_INK_DARK}
          >
            {inr(gain)} AI Credits
          </Amount>
        </SummaryRow>
        <SummaryRow>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            You pay
          </Typography>
          <Amount icon={CreditIcon}>{inr(total)} Credits</Amount>
        </SummaryRow>
        <SummaryDivider />
        <SummaryRow>
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
            Your Credits balance
          </Typography>
          <Typography
            sx={{
              fontSize: 12.5,
              fontWeight: 600,
              fontVariantNumeric: 'tabular-nums',
              color: short > 0 ? 'error.main' : 'text.secondary',
            }}
          >
            {inr(credits)}
          </Typography>
        </SummaryRow>
      </Stack>

      {short > 0 && (
        <ShortfallPanel>
          <ErrorOutlineRounded
            sx={{ fontSize: 16, color: 'error.main', mt: '1px' }}
          />
          <Stack sx={{ gap: 0.4 }}>
            <Typography
              sx={{ fontSize: 12.8, fontWeight: 700, color: 'error.main' }}
            >
              You&apos;re {inr(short)} Credits short
            </Typography>
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
              Earn Credits by finishing courses and competitions, or buy a
              Credits pack from the Shop.
            </Typography>
            <Typography
              component="button"
              onClick={onGetCredits}
              sx={{
                mt: 0.25,
                p: 0,
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                textAlign: 'left',
                fontSize: 12,
                fontWeight: 700,
                color: 'primary.main',
              }}
            >
              Get Credits →
            </Typography>
          </Stack>
        </ShortfallPanel>
      )}

      <BuyButton disabled={short > 0 || isLoading} onClick={onBuy}>
        {short > 0
          ? 'Not enough Credits'
          : `Buy ${inr(gain)} AI Credits${isLoading ? '…' : ''}`}
      </BuyButton>
    </Stack>
  );
};

export default BuyStep;
